import { Fragment, useState } from "react";
import { MenuItem, Select, SelectChangeEvent, TextField, Stack, Chip, Box, Typography, InputLabel, FormControl, Autocomplete } from "@mui/material";
import { CDashboardModulePageAction, CDashboardModuleTabComponent } from "../../DashboardApp/types";
import { componentsMap } from "../ComponentsMap";

interface ComponentPropertiesProps {
	component: CDashboardModuleTabComponent;
	componentUpdated(component: CDashboardModuleTabComponent): void;
	pageActions: CDashboardModulePageAction[];
}

const defaultSchema = [{ name: "displayCondition", label: "Display Condition", required: false, type: "string", options: undefined, defaultValue: undefined }];

const ComponentProperties: React.FC<ComponentPropertiesProps> = ({ component, componentUpdated, pageActions }) => {
	const [selectedComponent, setSelectedComponent] = useState(JSON.parse(JSON.stringify(component)));

	const handleFormChange = (name: string, value: unknown, update: boolean) => {
		const newProperties = { ...selectedComponent.properties, [name || ""]: value as string } as Record<string, string>;
		const updatedComp = { ...selectedComponent, properties: newProperties } as CDashboardModuleTabComponent;
		if (update) componentUpdated(updatedComp);
		setSelectedComponent(updatedComp);
	};

	const handleSelectComponentTypeFormChange = (value: string | null) => {
		if (value) {
			const updatedComp = JSON.parse(JSON.stringify({ ...component, properties: {}, type: value } as CDashboardModuleTabComponent));
			componentUpdated(updatedComp);
			setSelectedComponent(updatedComp);
		}
	};

	return (
		<Stack gap={2} sx={{ paddingTop: 2, paddingBottom: 2 }}>
			<FormControl
				fullWidth
				sx={{
					"& .MuiInputBase-root::before": {
						borderBottom: "1px dashed rgba(0, 0, 0, 0.42)",
					},
				}}
			>
				<Autocomplete
					sx={{
						"& .MuiInputBase-root": { backgroundColor: "white", paddingLeft: 0, paddingRight: 0 },
						"& .MuiInputBase-root:hover": { backgroundColor: "white" },
						"& .MuiFormLabel-root": {
							left: "-12px",
							top: "-12px",
						},
						"& .MuiFilledInput-input": {
							backgroundColor: "white",
							paddingLeft: 0,
							paddingRight: 0,
						},
					}}
					options={Object.keys(componentsMap).sort()}
					value={selectedComponent.type}
					onChange={(event: unknown, newValue: string | null) => handleSelectComponentTypeFormChange(newValue)}
					renderInput={(params) => <TextField variant="filled" {...params} label="Component Type" />}
				/>
			</FormControl>
			{[...componentsMap[selectedComponent.type].schema, ...defaultSchema].map((field) => (
				<Fragment key={field.name}>
					{field.type === "string" && (
						<FormControl
							fullWidth
							sx={{
								"& .MuiInputBase-root::before": {
									borderBottom: "1px dashed rgba(0, 0, 0, 0.42)",
								},
							}}
						>
							<TextField
								InputLabelProps={{
									shrink: true,
								}}
								variant="filled"
								label={field.label}
								name={field.name}
								value={(selectedComponent.properties && selectedComponent.properties[field.name]) || field.defaultValue || ""}
								onChange={(event) => handleFormChange(field.name, event.target.value, false)}
								onBlur={() => componentUpdated(selectedComponent)}
								required={field.required}
								sx={{
									"& .MuiFormLabel-root": {
										left: "-12px",
										top: "-12px",
									},
									"& .MuiFilledInput-input": {
										backgroundColor: "white",
										paddingLeft: 0,
										paddingRight: 0,
									},
								}}
							/>
						</FormControl>
					)}
					{field.type === "number" && (
						<FormControl
							fullWidth
							sx={{
								"& .MuiInputBase-root::before": {
									borderBottom: "1px dashed rgba(0, 0, 0, 0.42)",
								},
							}}
						>
							<TextField
								InputLabelProps={{
									shrink: true,
								}}
								variant="filled"
								type="number"
								label={field.label}
								name={field.name}
								value={(selectedComponent.properties && selectedComponent.properties[field.name]) || field.defaultValue || ""}
								onChange={(event) => handleFormChange(field.name, event.target.value, false)}
								onBlur={() => componentUpdated(selectedComponent)}
								required={field.required}
								sx={{
									"& .MuiFormLabel-root": {
										left: "-12px",
										top: "-12px",
									},
									"& .MuiFilledInput-input": {
										backgroundColor: "white",
										paddingLeft: 0,
										paddingRight: 0,
									},
									"& .MuiFilledInput-root:before": {
										borderBottom: "1px dashed rgba(0, 0, 0, 0.42)",
									},
								}}
							/>
						</FormControl>
					)}
					{field.type === "list" && (
						<FormControl
							fullWidth
							sx={{
								"& .MuiFormLabel-root": {
									left: "-12px",
									top: "0px",
								},
								"& .MuiInputBase-root::before": {
									borderBottom: "1px dashed rgba(0, 0, 0, 0.42)",
								},
							}}
						>
							<InputLabel>{field.label}</InputLabel>
							<Select
								variant="filled"
								value={selectedComponent.properties?.[field.name] || field.defaultValue}
								onChange={(event: SelectChangeEvent<string | number>) => {
									handleFormChange(field.name, event.target.value, true);
								}}
								sx={{
									"& .MuiFilledInput-input": {
										backgroundColor: "white",
										paddingLeft: 0,
										paddingRight: 0,
									},
									"& .MuiInputBase-root:hover": { backgroundColor: "white" },
								}}
							>
								{field.options?.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
					{field.type === "chips" && (
						<Box>
							<Typography variant={"caption"}>{field.label}</Typography>
							<Box sx={{ border: "1px solid #c4c4c4", padding: "10px" }}>
								{field.options?.map((option) => (
									<Chip
										onClick={() => {}}
										key={option}
										label={option}
										sx={{ marginRight: "5px", marginBottom: "5px" }}
										color={(selectedComponent.properties && selectedComponent.properties[field.name] === option) || ((!selectedComponent.properties || !selectedComponent.properties[field.name]) && field.defaultValue === option) ? "primary" : "default"}
									/>
								))}
							</Box>
						</Box>
					)}
					{field.type === "pageAction" && (
						<Box>
							<Typography variant={"caption"}>{field.label}</Typography>
							<FormControl
								fullWidth
								sx={{
									"& .MuiInputBase-root::before": {
										borderBottom: "1px dashed rgba(0, 0, 0, 0.42)",
									},
								}}
							>
								<Autocomplete
									sx={{
										"& .MuiInputBase-root": { backgroundColor: "white", paddingLeft: 0, paddingRight: 0 },
										"& .MuiInputBase-root:hover": { backgroundColor: "white" },
										"& .MuiFormLabel-root": {
											left: "-12px",
											top: "-12px",
										},
										"& .MuiFilledInput-input": {
											backgroundColor: "white",
											paddingLeft: 0,
											paddingRight: 0,
										},
									}}
									options={[...new Set(pageActions.map((action: { name: string }): string => action.name).sort())]}
									value={selectedComponent.properties?.[field.name] || field.defaultValue}
									onChange={(event: unknown, newValue: string | null) => {
										handleFormChange(field.name, newValue, true);
									}}
									renderInput={(params) => <TextField variant="filled" {...params} />}
								/>
							</FormControl>
							<FormControl
								fullWidth
								sx={{
									marginTop: 2,
									"& .MuiInputBase-root::before": {
										borderBottom: "1px dashed rgba(0, 0, 0, 0.42)",
									},
								}}
							>
								<TextField
									InputLabelProps={{
										shrink: true,
									}}
									variant="filled"
									label={field.label + " Payload"}
									value={(selectedComponent.properties && selectedComponent.properties[field.name + "_Payload"]) || field.defaultValue || ""}
									onChange={(event) => handleFormChange(field.name + "_Payload", event.target.value, false)}
									onBlur={() => componentUpdated(selectedComponent)}
									required={field.required}
									sx={{
										"& .MuiFormLabel-root": {
											left: "-12px",
											top: "-12px",
										},
										"& .MuiFilledInput-input": {
											backgroundColor: "white",
											paddingLeft: 0,
											paddingRight: 0,
										},
									}}
								/>
							</FormControl>
						</Box>
					)}
				</Fragment>
			))}
		</Stack>
	);
};

export default ComponentProperties;
